import {
	type BulletListDefinition,
	type ListItemDefinition,
	type OrderedListDefinition,
} from '@atlaskit/adf-schema';

import type { ParsedStructuredElementType } from '../../layout/types';
import type { AdfContentNode, RecursiveTagConverter } from '../types';

/**
 * Converts a Lumina list item node to an ADF list item node.
 * Recursively converts child nodes and wraps text in paragraphs.
 *
 * Returns undefined if the list item has no content.
 */
const convertListItem = (
	luminaNode: ParsedStructuredElementType,
	recursiveConvert: (node: ParsedStructuredElementType) => AdfContentNode | undefined,
): ListItemDefinition | undefined => {
	let children: (string | ParsedStructuredElementType)[];

	if (Array.isArray(luminaNode.children)) {
		children = luminaNode.children;
	} else if (typeof luminaNode.children === 'string') {
		children = [luminaNode.children];
	} else {
		children = [];
	}

	const content: AdfContentNode[] = [];

	for (const child of children) {
		if (typeof child === 'string') {
			content.push({
				type: 'paragraph',
				content: [{ type: 'text', text: child }],
			});
		} else {
			const converted = recursiveConvert(child as ParsedStructuredElementType);
			if (converted !== undefined) {
				content.push(converted);
			}
		}
	}

	if (content.length === 0) {
		return undefined;
	}

	return {
		type: 'listItem',
		content: content as ListItemDefinition['content'],
	};
};

/**
 * Converts a Lumina list (ul or ol) to an ADF list.
 *
 * This converter requires recursiveConvert to process child list items.
 * Returns undefined if the list has no items or all list items fail to convert.
 */
export const convertList: RecursiveTagConverter = (
	luminaNode,
	recursiveConvert,
): BulletListDefinition | OrderedListDefinition | undefined => {
	const children = Array.isArray(luminaNode.children) ? luminaNode.children : [];
	const content: ListItemDefinition[] = [];

	for (const child of children) {
		if (typeof child === 'string') {
			continue;
		}
		const converted = convertListItem(child, recursiveConvert);
		if (converted) {
			content.push(converted);
		}
	}

	if (content.length === 0) {
		return undefined;
	}

	return luminaNode.tag === 'ol'
		? { type: 'orderedList', content }
		: { type: 'bulletList', content };
};
